import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { isPrivateProductMediaKey, signMediaUrls } from "@/lib/media";
import { listingTemplatePreviewDisplayImages } from "@/lib/listing-template-preview-media";
import { listingTemplateProductFromCanonical } from "@/lib/template-product-adapter";
import { listingTemplatePreviewVariantsWithIntendedPrice } from "@/lib/listing-template-preview-pricing";
import { resolveTemplateForListing } from "@/lib/template-resolution";
import { templateById } from "@/lib/listing-templates";
import TemplateRenderer from "./TemplateRenderer";
import { BrowserFrame, PhoneFrame } from "./PreviewFrame";

/**
 * Preview a real listing rendered through its resolved template. Uses the
 * actual listing/product/variant/media data (via the safe adapter) and the same
 * resolution order as publication (override → connector default → platform).
 * The demo datasets in the review screen remain available separately.
 */
export default function RealListingPreview({ orgId, listingId }: { orgId: string; listingId: string }) {
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["real-listing-preview", orgId, listingId],
    enabled: !!orgId && !!listingId,
    queryFn: async () => {
      const { data: listing } = await supabase
        .from("channel_listings")
        .select(
          "id, product_id, connector_id, channel_title, channel_description, channel_short_description, channel_category_id, currency, price_strategy, provider_config, listing_template_override, connector:connectors(id, provider, listing_template_id)",
        )
        .eq("org_id", orgId)
        .eq("id", listingId)
        .maybeSingle();

      const l = listing as Record<string, unknown> | null;
      const productId = l?.product_id ? String(l.product_id) : null;
      const connector = l?.connector as
        | { id: string; provider: string | null; listing_template_id: string | null }
        | null;

      const [productRes, mediaRes, variantsRes] = await Promise.all([
        productId
          ? supabase
              .from("products")
              .select(
                "name, brand, description, short_description, sku, selling_price, condition, weight_kg, length_cm, width_cm, height_cm, manufacturer, model_number, mpn, product_type",
              )
              .eq("org_id", orgId)
              .eq("id", productId)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        supabase.rpc("get_effective_listing_media", { p_org_id: orgId, p_listing_id: listingId }),
        supabase
          .from("channel_listing_variants")
          .select("variant_id, included, channel_sku, channel_price, effective_channel_price, variants(id, sku, option_values, selling_price, weight_kg, length_cm, width_cm, height_cm)")
          .eq("org_id", orgId)
          .eq("channel_listing_id", listingId)
          .eq("included", true),
      ]);

      const media = (mediaRes.data ?? []) as {
        media_asset_id?: string | null;
        storage_key: string | null;
        alt_text?: string | null;
        public_url?: string | null;
        url?: string | null;
      }[];
      const privateKeys = media
        .map((m) => m.storage_key)
        .filter((key): key is string => Boolean(key) && isPrivateProductMediaKey(key));
      const signedUrlsByStorageKey = privateKeys.length ? await signMediaUrls(privateKeys) : {};

      return {
        listing: l,
        connector,
        product: productRes.data as Record<string, unknown> | null,
        media,
        signedUrlsByStorageKey,
        variants: (variantsRes.data ?? []) as {
          sku?: string | null;
          channel_sku?: string | null;
          channel_price?: number | null;
          effective_channel_price?: number | null;
          option_values?: Record<string, string> | null;
          selling_price?: number | null;
          variant?: {
            sku?: string | null;
            option_values?: Record<string, string> | null;
            selling_price?: number | null;
            weight_kg?: number | null;
            length_cm?: number | null;
            width_cm?: number | null;
            height_cm?: number | null;
          };
          variants?: {
            sku?: string | null;
            option_values?: Record<string, string> | null;
            selling_price?: number | null;
            weight_kg?: number | null;
            length_cm?: number | null;
            width_cm?: number | null;
            height_cm?: number | null;
          };
        }[],
      };
    },
  });

  const resolution = useMemo(() => {
    const l = data?.listing;
    const connector = data?.connector;
    return resolveTemplateForListing({
      listingOverride: (l?.listing_template_override as string | null | undefined) ?? null,
      connectorDefault: connector?.listing_template_id ?? null,
      provider: connector?.provider ?? null,
    });
  }, [data?.listing, data?.connector]);

  const product = useMemo(() => {
    if (!data) return null;
    const l = data.listing ?? {};
    const p = data.product ?? {};
    const media = listingTemplatePreviewDisplayImages(
      data.media ?? [],
      data.signedUrlsByStorageKey ?? {},
    ).map((m) => ({ url: m.url, alt: m.alt }));
    const variants = (data.variants ?? []).map((v) => {
      const nested = (v.variant ?? v.variants) as
        | {
          sku?: string | null;
          option_values?: Record<string, string> | null;
          selling_price?: number | null;
          weight_kg?: number | null;
          length_cm?: number | null;
          width_cm?: number | null;
          height_cm?: number | null;
        }
        | undefined;
      return {
        sku: v.channel_sku ?? nested?.sku ?? v.sku ?? null,
        option_values: nested?.option_values ?? v.option_values ?? null,
        selling_price: nested?.selling_price ?? v.selling_price ?? null,
        channel_price: v.channel_price ?? null,
        effective_channel_price: v.effective_channel_price ?? null,
        variant: nested
          ? {
            sku: nested.sku,
            option_values: nested.option_values,
            selling_price: nested.selling_price,
            weight_kg: nested.weight_kg,
            length_cm: nested.length_cm,
            width_cm: nested.width_cm,
            height_cm: nested.height_cm,
          }
          : null,
      };
    });
    const pricedVariants = listingTemplatePreviewVariantsWithIntendedPrice({
      priceStrategy: (l.price_strategy as string | null | undefined) ?? null,
      providerConfig: (l.provider_config as Record<string, unknown> | null | undefined) ?? null,
      productSellingPrice: (p.selling_price as number | null | undefined) ?? null,
      variants,
    });
    return listingTemplateProductFromCanonical({
      product: p,
      listing: l,
      media,
      variants: pricedVariants,
      currency: (l.currency as string | null) ?? null,
    });
  }, [data]);

  if (isLoading) {
    return (
      <div className="p-8 text-center text-sm text-slate-400">
        <i className="ri-loader-4-line animate-spin" /> Loading preview…
      </div>
    );
  }

  if (isError || !data?.listing) {
    return (
      <div className="p-8 text-center text-sm text-slate-500">
        Could not load this listing&apos;s preview.
      </div>
    );
  }

  const templateMeta = templateById(resolution.templateId);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Preview with selected template</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Showing <strong>{templateMeta?.name ?? "—"}</strong> (
            {resolution.source === "listing_override"
              ? "listing override"
              : resolution.source === "connector_default"
                ? "account default"
                : "platform default"}
            ). Uses this listing&apos;s real title, description, media and variants.
            Private product images are shown with a temporary signed URL for preview only;
            publication copies them to durable public HTTPS URLs.
          </p>
        </div>
        <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg p-1 w-fit">
          <button
            onClick={() => setDevice("desktop")}
            className={`px-3 py-1.5 text-sm font-medium rounded-md flex items-center gap-1.5 whitespace-nowrap ${
              device === "desktop" ? "bg-white text-slate-800 border border-slate-200" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <i className="ri-computer-line" /> Desktop
          </button>
          <button
            onClick={() => setDevice("mobile")}
            className={`px-3 py-1.5 text-sm font-medium rounded-md flex items-center gap-1.5 whitespace-nowrap ${
              device === "mobile" ? "bg-white text-slate-800 border border-slate-200" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <i className="ri-smartphone-line" /> Mobile
          </button>
        </div>
      </div>

      <div className="rounded-xl bg-slate-100 border border-slate-200 p-4 sm:p-6">
        {device === "desktop" ? (
          <div className="mx-auto max-w-4xl">
            <BrowserFrame>
              <TemplateRenderer templateId={resolution.templateId ?? "clean-commerce"} product={product!} />
            </BrowserFrame>
          </div>
        ) : (
          <div className="flex justify-center">
            <PhoneFrame>
              <TemplateRenderer templateId={resolution.templateId ?? "clean-commerce"} product={product!} />
            </PhoneFrame>
          </div>
        )}
      </div>
    </div>
  );
}